/// <amd-module name="Repos/Changes/Components/ChangeDiffContent" />
import * as React from "react";
import { useChangesContext } from "Repos/ChangesCommon/ChangesContext";
import { DiffViewerType, IChange, IChangeItemContent } from "Repos/ChangesCommon/Contracts";
import { IDiffViewerProps, IEditorSelection } from "Repos/Common/Contracts/Editor";
import { DiffViewerOrientation } from "Repos/Common/Contracts/VersionControlViewModel";
import { IReadonlyObservableArray, IReadonlyObservableValue } from "VSS/Core/Observable";
import { IEditorWidgetRegistration } from "VSS/Features/Monaco/Contracts";
import { PerformanceScenarioContext } from "VSS/Platform/Components/Performance";
import { WrappedComponent, usePageContext } from "VSS/Platform/Layout";
import { Card } from "VSSUI/Card";
import { Observer } from "VSSUI/Observer";
import { css } from "VSSUI/Util";
import { FileDiscussionWidget } from "./Discussion/FileDiscussionWidget";
import { HistoryBehavior, IPageStateHistoryService } from "VSS/Features/History";
import { getSelectionFromQueryParams } from "../Util";

interface ICompareVersion<TVersionDescriptor> {
    content: IReadonlyObservableValue<IChangeItemContent | undefined>;
    path: string;
    version: TVersionDescriptor;
}

export interface IChangeDiffContentProps<TVersionDescriptor> {
    /**
     * If true, keyboard shortcuts for next and previous difference will be added to Monaco
     */
    addDiffKeyboardShortcuts: boolean;

    /**
     * The base version to compare files against
     */
    compareVersion: TVersionDescriptor;

    /**
     * Gets the currently diff orientation
     */
    diffViewerOrientation: IReadonlyObservableValue<DiffViewerOrientation>;

    /**
     * List of custom widget registrations for the diff content viewer which uses Monaco diff editor.
     */
    diffWidgets?: IEditorWidgetRegistration[] | IReadonlyObservableArray<IEditorWidgetRegistration>;

    /**
     * For images only, if true pixel diff will be displayed and orientation will be ignored.
     */
    isPixelDiff: boolean;

    /**
     * Gets the currently selected item (file or folder)
     */
    selectedItem: IChange;

    /**
     * If set, scroll to the specified line number.
     */
    scrollToLine?: IReadonlyObservableValue<number | undefined>;

    /**
     * Gets the url to download the specified file at the specified version
     *
     * @param path Full path of the file
     * @param version Version at which to get the file
     */
    getFileDownloadUrl(path: string, version: TVersionDescriptor): string;
}

export function ChangeDiffContent<TVersionDescriptor>(props: IChangeDiffContentProps<TVersionDescriptor>) {
    const { addDiffKeyboardShortcuts, compareVersion, diffViewerOrientation, diffWidgets, getFileDownloadUrl, selectedItem, scrollToLine } = props;

    const { filesService } = useChangesContext<TVersionDescriptor>();

    return (
        <PerformanceScenarioContext.Consumer>
            {scenarioProps => {
                const pendingWorkComplete = scenarioProps.registerPendingWork("load-file-diff-viewer")!;
                const modifiedVersion = filesService.convertVersionToDescriptor(selectedItem.commitId);
                const previousVersion = compareVersion;

                const modifiedCompareVersion: ICompareVersion<TVersionDescriptor> = {
                    content: filesService.getItemContent(selectedItem.path, modifiedVersion),
                    path: selectedItem.path,
                    version: modifiedVersion
                };

                const originalPath = selectedItem.sourcePath || selectedItem.path;
                // Edge case: for Previous to Shelvesets versions, we must use the modified path even when requesting the original content.
                // Server will resolve the original path. Otherwise it will fail in TfsVersionControlProvider.GetShelvedChanges().
                const originalPathToFetch = filesService.convertDescriptorToVersion(previousVersion)?.startsWith("PS")
                    ? selectedItem.path
                    : originalPath;

                const originalCompareVersion: ICompareVersion<TVersionDescriptor> = {
                    content: filesService.getItemContent(originalPathToFetch, previousVersion),
                    path: originalPath,
                    version: previousVersion
                };

                return (
                    <CompareDiffContent
                        addDiffKeyboardShortcuts={addDiffKeyboardShortcuts}
                        diffViewerOrientation={diffViewerOrientation}
                        diffWidgets={diffWidgets}
                        getFileDownloadUrl={getFileDownloadUrl}
                        isPixelDiff={props.isPixelDiff}
                        modifiedVersion={modifiedCompareVersion}
                        originalVersion={originalCompareVersion}
                        onLoaded={pendingWorkComplete && pendingWorkComplete.complete}
                        scrollToLine={scrollToLine}
                    />
                );
            }}
        </PerformanceScenarioContext.Consumer>
    );
}

interface ICompareDiffContentProps<TVersionDescriptor> {
    addDiffKeyboardShortcuts: boolean;
    diffViewerOrientation: IReadonlyObservableValue<DiffViewerOrientation>;
    diffWidgets?: IEditorWidgetRegistration[] | IReadonlyObservableArray<IEditorWidgetRegistration>;
    isPixelDiff: boolean;
    modifiedVersion: ICompareVersion<TVersionDescriptor>;
    originalVersion: ICompareVersion<TVersionDescriptor>;
    scrollToLine?: IReadonlyObservableValue<number | undefined>;
    getFileDownloadUrl(path: string, version: TVersionDescriptor): string;
    onLoaded(): void;
}

function CompareDiffContent<TVersionDescriptor>(props: ICompareDiffContentProps<TVersionDescriptor>): JSX.Element {

    const pageContext = usePageContext();

    const { filesService } = useChangesContext<TVersionDescriptor>();

    
    const selection = getSelectionFromQueryParams(pageContext);
    
    if (selection?.orientation) { 
        filesService.setDiffOrientation(DiffViewerType.detail, selection.orientation as DiffViewerOrientation);
    }

    return (
        <Observer originalContent={props.originalVersion.content} modifiedContent={props.modifiedVersion.content}>
            {(contentProps: { originalContent: IChangeItemContent | undefined; modifiedContent: IChangeItemContent | undefined }) => {
                const getModifiedUrl = () => props.getFileDownloadUrl(props.modifiedVersion.path, props.modifiedVersion.version);
                let getOriginalUrl = () => props.getFileDownloadUrl(props.originalVersion.path, props.originalVersion.version);

                const modifiedContent = contentProps.modifiedContent;
                let originalContent = contentProps.originalContent;

                if (originalContent && originalContent.error) {
                    // There is no previous version for this item.
                    originalContent = modifiedContent;
                    getOriginalUrl = getModifiedUrl;
                }

                let fileDiscussionElement: JSX.Element | null = null;
                const nonBinaryContent = modifiedContent && !modifiedContent.details.metadata.isBinary;
                if (originalContent && modifiedContent && !nonBinaryContent) {
                    fileDiscussionElement = <FileDiscussionWidget className="margin-left-16 margin-top-16" widgets={props.diffWidgets} />;
                }

                return (
                    <Card
                        className={css("no-padding", nonBinaryContent && "flex-grow flex-noshrink v-scroll-hidden")}
                        contentProps={{ className: "relative", contentPadding: false }}
                    >
                        {originalContent && modifiedContent ? (
                            <div className={css("flex-column scroll-hidden", nonBinaryContent && "absolute-fill")}>
                                {fileDiscussionElement}
                                <WrappedComponent<IDiffViewerProps>
                                    wrappedType="repos-diff-viewer"
                                    dependencies={["ms.vss-code-web.code-editor"]}
                                    wrappedProps={{
                                        addDiffKeyboardShortcuts: props.addDiffKeyboardShortcuts,
                                        className: "flex-row flex-grow",
                                        diffOrientation: props.diffViewerOrientation,
                                        diffWidgets: props.diffWidgets,
                                        editable: false,
                                        editorClassName: "repos-commit-editor-background scroll-hidden",
                                        getModifiedDownloadUrl: getModifiedUrl,
                                        getOriginalDownloadUrl: getOriginalUrl,
                                        isPixelDiff: props.isPixelDiff,
                                        modifiedContent: modifiedContent.details,
                                        scrollToLine: props.scrollToLine,
                                        onLoaded: props.onLoaded,
                                        originalContent: originalContent.details,
                                        showMarginForIcons: Boolean(props.diffWidgets),
                                        initialSelectedText: selection
                                    }}
                                />
                            </div>
                        ) : (
                            <div className="repos-commit-editor-background flex-grow" />
                        )}
                    </Card>
                );
            }}
        </Observer>
    );
}
